import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AiOutlineRobot } from 'react-icons/ai';
import { RiMenu4Line } from 'react-icons/ri';
import { IoClose } from 'react-icons/io5';
import { FiArrowLeft, FiBook, FiBookOpen, FiCheckCircle, FiLock } from 'react-icons/fi';
import axios from 'axios';
import { BASELINE } from '../../util';

// Import CSS files for styling
import './tufte.css';

// Progress Item for the sidebar
const ProgressItem = ({ title, progress, isAIEnabled, isActive, onClick, isLocked, type }) => (
  <div
    onClick={isLocked ? undefined : onClick}
    className={`
      p-6 m-2 rounded-lg border border-gray-700
      ${isLocked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
      ${isActive ? 'bg-gray-800' : !isLocked && 'hover:bg-gray-800 hover:border-gray-600'}
      transition-all duration-200
    `}
  >
    <div className="flex justify-between items-center mb-4">
      <div className="flex items-center">
        {type === 'quiz' ? (
          <FiCheckCircle size={18} className="text-green-400 mr-2" />
        ) : (
          <FiBook size={18} className="text-blue-400 mr-2" />
        )}
        <h3 className="text-lg font-semibold text-white">{title}</h3>
      </div>
      {isAIEnabled && (
        <div className="flex items-center bg-blue-900 bg-opacity-30 px-3 py-1 rounded-full">
          <AiOutlineRobot size={20} className="text-blue-400 mr-1" />
          <span className="text-sm text-blue-400 font-medium">AI</span>
        </div>
      )}
    </div>

    <div className="flex items-center">
      <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden">
        <div
          className="h-full bg-blue-500 rounded-full"
          style={{ width: `${progress}%` }}
        />
      </div>
      <span className="ml-3 text-sm font-medium text-gray-300">{progress}%</span>
    </div>
  </div>
);

// Mobile sidebar component
const ProgressSidebar = ({ isOpen, onClose, courseData, currentCourse, currentLesson, onCourseSelect, onLessonSelect }) => (
  <div
    className={`
      fixed inset-y-0 left-0 w-64 bg-[#1F1F1F] z-50 transform transition-transform duration-300 ease-in-out
      ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      md:hidden
    `}
  >
    {/* Overlay */}
    {isOpen && (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />
    )}

    {/* Sidebar Content */}
    <div className="relative z-50 h-full overflow-y-auto">
      <div className="flex justify-between items-center p-4 border-b border-[#2B2B2B]">
        <h2 className="text-lg font-bold text-white">Course Review</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <IoClose size={24} />
        </button>
      </div>

      {courseData.map((course) => (
        <div key={course.id}>
          <div
            className="p-4 bg-[#252525] border-b border-[#2B2B2B] font-bold cursor-pointer hover:bg-[#303030]"
            onClick={() => onCourseSelect(course.id)}
          >
            {course.name}
          </div>

          {currentCourse === course.id && course.lessons && course.lessons.map((lesson) => (
            <div
              key={lesson.id}
              className={`p-3 pl-8 border-b border-[#2B2B2B] cursor-pointer ${currentLesson === lesson.id ? 'bg-[#303030]' : 'hover:bg-[#252525]'}`}
              onClick={() => onLessonSelect(course.id, lesson.id)}
            >
              <div className="flex items-center">
                {lesson.type === 'quiz' ? (
                  <FiCheckCircle size={16} className="text-green-400 mr-2" />
                ) : (
                  <FiBookOpen size={16} className="text-blue-400 mr-2" />
                )}
                <span>{lesson.title}</span>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
);

// HTMLContent component to handle HTML rendering with KaTeX support
const HTMLContent = ({ htmlPath }) => {
  const [content, setContent] = useState('');
  const [error, setError] = useState(null);
  const contentRef = useRef(null);

  // Load HTML content and head links
  useEffect(() => {
    const fetchHTML = async () => {
      try {
        const url = `Class_Materials/${htmlPath}?t=${new Date().getTime()}`;
        const response = await axios.get(BASELINE + url, { responseType: 'text' });
        const text = response.data;

        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');

        // If <head> exists in the fetched document, load its link tags (e.g., fonts and styles)
        const headLinks = doc.head.querySelectorAll('link[rel="stylesheet"]');
        headLinks.forEach(link => {
          const href = link.getAttribute('href');
          // Check if the link is already present in document.head
          if (!document.querySelector(`link[href="${href}"]`)) {
            const newLink = document.createElement('link');
            newLink.rel = 'stylesheet';
            newLink.href = href;
            // Copy integrity and crossorigin if available
            if (link.getAttribute('integrity')) {
              newLink.integrity = link.getAttribute('integrity');
            }
            if (link.getAttribute('crossorigin')) {
              newLink.crossOrigin = link.getAttribute('crossorigin');
            }
            document.head.appendChild(newLink);
          }
        });

        // Extract body content if available, otherwise use full text
        let bodyContent = text;
        if (text.includes('<body>') && text.includes('</body>')) {
          const bodyMatch = text.match(/<body[^>]*>([\s\S]*)<\/body>/i);
          if (bodyMatch && bodyMatch[1]) {
            bodyContent = bodyMatch[1];
          }
        }
        setContent(bodyContent);
      } catch (err) {
        console.error('Error fetching HTML:', err);
        setError(err.message);
      }
    };

    if (htmlPath) {
      fetchHTML();
    }
  }, [htmlPath]);

  // Setup solution toggle functionality
  useEffect(() => {
    // 솔루션 토글 함수: 특정 ID를 가진 해답 영역을 토글합니다.
    window.solutionToggle = (btn, qNum) => {
      const solutionElem = document.getElementById(`question-solution-${qNum}`);
      if (solutionElem) {
        // 현재 display 상태에 따라 토글
        if (solutionElem.style.display === "none" || solutionElem.style.display === "") {
          solutionElem.style.display = "block";
          btn.textContent = "-";
        } else {
          solutionElem.style.display = "none";
          btn.textContent = "+";
        }
      }
    };

    // 정답 확인 함수: 이전 형제 요소(문제 테이블)의 모든 입력값을 확인하여 스타일을 변경합니다.
    window.checkAnswers = (btn) => {
      const matchingTable = btn.previousElementSibling;
      if (matchingTable) {
        const inputs = matchingTable.querySelectorAll('.matching-input');
        
        inputs.forEach(input => {
          const correctAnswer = input.getAttribute('data-correct');
          const userAnswer = input.value.toUpperCase();
          
          if (userAnswer === correctAnswer) {
            input.style.backgroundColor = '#34D399';
            input.style.color = '#064E3B';
            input.style.borderColor = '#34D399';
          } else {
            input.style.backgroundColor = '#F87171';
            input.style.color = '#7F1D1D';
            input.style.borderColor = '#F87171';
          }
        });
      }
    };
  }, []);

  // Load KaTeX scripts and styles
  useEffect(() => {
    // Add KaTeX CSS if not already present
    if (!document.querySelector('link[href*="katex.min.css"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/katex@0.16.19/dist/katex.min.css';
      link.integrity = 'sha384-7lU0muIg/i1plk7MgygDUp3/bNRA65orrBub4/OSWHECgwEsY83HaS1x3bljA/XV';
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    }

    const loadKatex = () => {
      if (!window.katex) {
        return new Promise((resolve) => {
          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/npm/katex@0.16.19/dist/katex.min.js';
          script.integrity = 'sha384-RdymN7NRJ+XoyeRY4185zXaxq9QWOOx3O7beyyrRK4KQZrPlCDQQpCu95FoCGPAE';
          script.crossOrigin = 'anonymous';
          script.onload = resolve;
          document.head.appendChild(script);
        });
      }
      return Promise.resolve();
    };

    const loadAutoRender = () => {
      if (!window.renderMathInElement) {
        return new Promise((resolve) => {
          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/npm/katex@0.16.19/dist/contrib/auto-render.min.js';
          script.integrity = 'sha384-hCXGrW6PitJEwbkoStFjeJxv+fSOOQKOPbJxSfM6G5sWZjAyWhXiTIIAmQqnlLlh';
          script.crossOrigin = 'anonymous';
          script.onload = resolve;
          document.head.appendChild(script);
        });
      }
      return Promise.resolve();
    };

    const renderMath = () => {
      if (contentRef.current && window.renderMathInElement) {
        try {
          window.renderMathInElement(contentRef.current, {
            delimiters: [
              { left: '$$', right: '$$', display: true },
              { left: '$', right: '$', display: false },
              { left: '\\(', right: '\\)', display: false },
              { left: '\\[', right: '\\]', display: true }
            ],
            throwOnError: false,
          });
        } catch (err) {
          console.error('Error rendering math:', err);
        }
      }
    };

    if (content && contentRef.current) {
      Promise.resolve()
        .then(loadKatex)
        .then(loadAutoRender)
        .then(() => {
          setTimeout(renderMath, 100);
        })
        .catch(err => {
          console.error('Error loading KaTeX:', err);
        });
    }
  }, [content]);

  if (error) {
    return <div>Error loading content: {error}</div>;
  }

  if (!content) {
    return <div>Loading...</div>;
  }

  return (
    <div className="w-full overflow-x-auto">
      <div className="relative">
        <div
          ref={contentRef}
          className="content prose max-w-none"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </div>
    </div>
  );
};

// LessonContent component to display lesson content
const LessonContent = ({ course, lesson }) => {
  if (!lesson) return <div className="text-center p-8">Select a lesson to review</div>;

  // Determine HTML path based on lesson title or other properties
  const getHtmlPath = () => {
    // Sanitize the lesson title for use in file path (replace spaces with underscores)
    const sanitizedTitle = lesson.title.replace(/\s+/g, '_');

    // You might want to adjust this path based on your actual file structure
    return `Math/${course}/${sanitizedTitle}.html`;
  };

  return (
    <div className="prose prose-invert max-w-none">
      <h2 className="text-2xl font-bold mb-4">{lesson.title}</h2>

      {lesson.type === 'quiz' ? (
        <div>
          <div className="bg-blue-900 bg-opacity-20 p-4 rounded-lg mb-4">
            <h3 className="text-lg font-semibold text-blue-400 mb-2">Quiz Summary</h3>
            <p>This quiz contains {lesson.questions || 'multiple'} questions.</p>
            {/* Quiz summary stats could go here */}
          </div>
          {/* Use HTMLContent for quiz content too */}
          <HTMLContent htmlPath={getHtmlPath()} />
        </div>
      ) : (
        // Use the HTMLContent component for non-quiz content
        <HTMLContent htmlPath={getHtmlPath()} />
      )}
    </div>
  );
};

// Course summary component
const CourseSummary = ({ course }) => {
  if (!course) return <div className="text-center p-8">Select a course to review</div>;

  // Calculate progress stats
  const totalLessons = course.lessons?.length || 0;
  const completedLessons = course.lessons?.filter(lesson => lesson.progress === 100).length || 0;
  const progressPercentage = totalLessons ? Math.round((completedLessons / totalLessons) * 100) : 0;

  return (
    <div className="prose prose-invert max-w-none">
      <h2 className="text-2xl font-bold mb-4">{course.name}</h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-[#252525] p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-400 mb-2">Progress</h3>
          <div className="flex items-center mb-2">
            <div className="w-full h-4 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <span className="ml-3 font-medium text-gray-300">{progressPercentage}%</span>
          </div>
          <p className="text-sm text-gray-400">{completedLessons} of {totalLessons} lessons completed</p>
        </div>

        <div className="bg-[#252525] p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-green-400 mb-2">Quizzes</h3>
          <p className="text-2xl font-bold">{course.lessons?.filter(lesson => lesson.type === 'quiz').length || 0}</p>
          <p className="text-sm text-gray-400">Total quizzes in this course</p>
        </div>

        <div className="bg-[#252525] p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-yellow-400 mb-2">Created</h3>
          <p className="text-md">{new Date(course.created).toLocaleDateString()}</p>
          <p className="text-sm text-gray-400">Course start date</p>
        </div>
      </div>

      <div className="bg-[#252525] p-4 rounded-lg mb-6">
        <h3 className="text-lg font-semibold text-white mb-2">Course Description</h3>
        <p>{course.context || 'No description available for this course.'}</p>
      </div>

      <h3 className="text-xl font-semibold mb-3">Lesson Overview</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {course.lessons?.map(lesson => (
          <div key={lesson.id} className="bg-[#252525] p-4 rounded-lg border border-gray-700">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center">
                {lesson.type === 'quiz' ? (
                  <FiCheckCircle size={18} className="text-green-400 mr-2" />
                ) : (
                  <FiBookOpen size={18} className="text-blue-400 mr-2" />
                )}
                <h4 className="font-semibold">{lesson.title}</h4>
              </div>
              <span className={`text-sm px-2 py-1 rounded-full ${lesson.progress === 100 ? 'bg-green-900 text-green-300' : 'bg-gray-700 text-gray-300'}`}>
                {lesson.progress === 100 ? 'Complete' : 'In Progress'}
              </span>
            </div>
            <p className="text-sm text-gray-400 truncate">{lesson.topic || 'General'}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const ReviewCourse = () => {
  const { cid, lid } = useParams();
  const navigate = useNavigate();
  const [courseData, setCourseData] = useState([]);
  const [currentCourse, setCurrentCourse] = useState(cid ? parseInt(cid) : null);
  const [currentLesson, setCurrentLesson] = useState(lid ? parseInt(lid) : null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeCourse, setActiveCourse] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);

  // Fetch courses and lessons data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all courses
        const coursesResponse = await axios.get(`${BASELINE}api/review-courses`);
        if (coursesResponse.data.success) {
          // Process course data
          const courses = coursesResponse.data.courses;

          // If we have a current course ID, ensure it's selected
          if (currentCourse) {
            const selected = courses.find(course => course.id === parseInt(currentCourse));
            if (selected) {
              setActiveCourse(selected);

              // If we have a lesson ID, find and set the active lesson
              if (currentLesson) {
                const selectedLesson = selected.lessons?.find(lesson => lesson.id === currentLesson);
                if (selectedLesson) {
                  setActiveLesson(selectedLesson);
                }
              }
            } else if (courses.length > 0) {
              setCurrentCourse(courses[0].id);
              setActiveCourse(courses[0]);
            }
          } else if (courses.length > 0) {
            setCurrentCourse(courses[0].id);
            setActiveCourse(courses[0]);
          }

          setCourseData(courses);
        }
      } catch (error) {
        console.error("Error fetching course data:", error);
      }
    };

    fetchData();
  }, [currentCourse, currentLesson]);

  // Handle course selection
  const handleCourseSelect = (courseId) => {
    setCurrentCourse(courseId);
    setCurrentLesson(null);

    // Find the selected course and set as active
    const selected = courseData.find(course => course.id === courseId);
    if (selected) {
      setActiveCourse(selected);
      setActiveLesson(null);
    }

    // Close mobile sidebar
    setIsSidebarOpen(false);
  };

  // Handle lesson selection
  const handleLessonSelect = (courseId, lessonId) => {
    setCurrentCourse(courseId);
    setCurrentLesson(lessonId);

    // Find the selected course and lesson
    const selectedCourse = courseData.find(course => course.id === courseId);
    if (selectedCourse) {
      setActiveCourse(selectedCourse);

      const selectedLesson = selectedCourse.lessons?.find(lesson => lesson.id === lessonId);
      if (selectedLesson) {
        setActiveLesson(selectedLesson);
      }
    }

    // Close mobile sidebar
    setIsSidebarOpen(false);
  };

  // Back to courses button handler
  const handleBackToCourses = () => {
    setCurrentLesson(null);
    setActiveLesson(null);
  };

  return (
    <div className="flex flex-col md:flex-row h-full bg-[#1a1a1a] text-white">
      {/* Desktop Sidebar */}
      <div className="hidden md:block w-2/12 bg-[#1F1F1F] border-r border-[#2B2B2B] overflow-y-auto scrollbar-thin scrollbar-track-[#2B2B2B] scrollbar-thumb-[#5c8dff]">
        {courseData.map((course) => (
          <div key={course.id}>
            <div
              className={`p-4 bg-[#252525] border-b border-[#2B2B2B] font-bold cursor-pointer hover:bg-[#303030] ${currentCourse === course.id && !currentLesson ? 'bg-[#303030]' : ''}`}
              onClick={() => handleCourseSelect(course.id)}
            >
              {course.name}
            </div>

            {currentCourse === course.id && course.lessons && course.lessons.map((lesson) => (
              <div
                key={lesson.id}
                className={`p-3 pl-8 border-b border-[#2B2B2B] cursor-pointer ${currentLesson === lesson.id ? 'bg-[#303030]' : 'hover:bg-[#252525]'}`}
                onClick={() => handleLessonSelect(course.id, lesson.id)}
              >
                <div className="flex items-center">
                  {lesson.type === 'quiz' ? (
                    <FiCheckCircle size={16} className="text-green-400 mr-2" />
                  ) : (
                    <FiBookOpen size={16} className="text-blue-400 mr-2" />
                  )}
                  <span className="truncate">{lesson.title}</span>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Mobile Sidebar */}
      <ProgressSidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        courseData={courseData}
        currentCourse={currentCourse}
        currentLesson={currentLesson}
        onCourseSelect={handleCourseSelect}
        onLessonSelect={handleLessonSelect}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="h-16 border-b border-[#2B2B2B] flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="md:hidden text-gray-400 hover:text-white transition-colors"
            >
              <RiMenu4Line size={24} />
            </button>

            <div className="flex items-center">
              {/* Back button to main lesson page */}
              <button
                onClick={() => navigate(`/dev/class/${currentCourse}/lesson`)}
                className="flex items-center bg-[#2B2B2B] hover:bg-[#3B3B3B] text-white px-3 py-1 rounded mr-4"
              >
                <FiArrowLeft size={16} className="mr-1" />
                <span className="text-sm">Back to Lessons</span>
              </button>

              {currentLesson && (
                <button
                  onClick={handleBackToCourses}
                  className="flex items-center text-gray-400 hover:text-white mr-3"
                >
                  <FiArrowLeft size={18} className="mr-1" />
                  <span className="text-sm">Back to Course</span>
                </button>
              )}
              <h1 className="text-xl font-bold truncate">
                {activeLesson ? activeLesson.title : activeCourse ? activeCourse.name : 'Course Review'}
              </h1>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 p-6 overflow-y-auto scrollbar-thin scrollbar-track-[#2B2B2B] scrollbar-thumb-[#5c8dff]">
          {currentLesson ? (
            <LessonContent course={activeCourse?.name} lesson={activeLesson} />
          ) : (
            <CourseSummary course={activeCourse} />
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewCourse;